import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Listbox } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  SignalSlashIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { components } from 'react-select';
import { ChevronDown } from 'lucide-react';
import { TbTargetArrow } from "react-icons/tb";
import { RiCalendarScheduleLine } from "react-icons/ri";
import { EyeIcon as ViewfinderCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './theme.css';

const API_BASE = '/api';

const IndicatorSeparator = () => null;

  // Optional: custom dropdown indicator
  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <ChevronDown className="text-[var(--text-primary)]" size={14} />
    </components.DropdownIndicator>
  );

const statusConfig = {
  active: {
    label: 'Active',
    color: 'text-[var(--status-success)]',
    icon: TbTargetArrow,
    badge: 'border border-[var(--status-success)] text-[var(--status-success)]',
  },
  scheduled: {
    label: 'Scheduled',
    color: 'text-[var(--status-warn)]',
    icon: RiCalendarScheduleLine,
    badge: 'border border-[var(--status-warn)] text-[var(--status-warn)]',
  },
  completed: {
    label: 'Completed',
    color: 'text-[var(--text-muted)]',
    icon: TbTargetArrow,
    badge: 'border border-[var(--text-muted)] text-[var(--text-muted)]',
  },
};

const StatusBadge = ({ status }) => {
  const { icon: Icon, badge, color, label } = statusConfig[status] || {};
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${badge}`}>
      <Icon className={`w-4 h-4 ${color}`} />
      {label || 'Unknown'}
    </span>
  );
};

export default function MissionsPage({ token = 'demo-token', role = 'admin' }) {
  const [missions, setMissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createForm, setCreateForm] = useState({ title: '', description: '', status: '', start_time: '', end_time: '', users: [], sensors: [], tags: []});
  const [editForm, setEditForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sortKey, setSortKey] = useState('title');
  const [sortAsc, setSortAsc] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchMissions();
    fetchUsers();
    fetchSensors();
    fetchTags();
  }, []);


  const fetchMissions = async () => {
    const res = await axios.get(`${API_BASE}/missions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setMissions(res.data);
  };

  const fetchUsers = async () => {
    const res = await axios.get(`${API_BASE}/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUsers(res.data);
  };

  const fetchSensors = async () => {
    const res = await axios.get(`${API_BASE}/sensors`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setSensors(res.data);
  };

  const fetchTags = async () => {
    const res = await axios.get(`${API_BASE}/tags`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setTags(res.data);
  };

  const handleCreate = async () => {
    const res = await axios.post(`${API_BASE}/admin/mission`, createForm, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCreateForm({ title: '', description: '', status:'', start_time: '', end_time: '', users: [], sensors: [], tags: []  });
    setShowCreateForm(false);
    fetchMissions();
  };

  const startEdit = (mission) => {
    setEditing(mission);
    if (!mission) return;
    setEditForm({
      title: mission.title,
      description: mission.description,
      start_time: mission.start_time,
      end_time: mission.end_time,
      users: mission.users || [],
      sensors: mission.sensors || [],
      tags: mission.tags || []
    });
  };


  const handleEdit = async () => {
    const missionToEdit = missions.find(m => m.id === editing.id);

    if (missionToEdit) {
      const updatedMission = {
      id: editing.id,
      title: editForm.title,
      status: missionToEdit.status,
      description: editForm.description,
      start_time: missionToEdit.start_time,
      end_time: missionToEdit.end_time,
    };

      await axios.put(`${API_BASE}/admin/mission/${editing.id}`, updatedMission, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchMissions();
    }

    // Close form in either case
    setEditing(null);
    setEditForm(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mission?')) return;
    await axios.delete(`${API_BASE}/admin/mission/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchMissions();
  };

  const toggleSort = (key) => {
    if (key === sortKey) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const filteredMissions = missions
    .filter((m) => `${m.title} ${m.description}`.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((m) => !statusFilter || m.status === statusFilter)
    .sort((a, b) => {
      const aVal = a[sortKey]?.toString().toLowerCase() || '';
      const bVal = b[sortKey]?.toString().toLowerCase() || '';
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

  const statuses = Object.keys(statusConfig);


  const statusCounts = {
    total: missions.length,
    active: missions.filter(m => m.status === 'active').length,
    scheduled: missions.filter(m => m.status === 'scheduled').length,
    completed: missions.filter(m => m.status === 'completed').length,
  };

  const userOptions = users.map(u => ({ value: u.id, label: u.email }));
  const sensorOptions = sensors.map(s => ({ value: s.id, label: s.name }));
  const tagOptions = tags.map(t => ({ value: t.id, label: t.label }));
  const selectedTags = createForm.tags.map(tag => {
    // Try to find tag in tagOptions by id
    const found = tagOptions.find(option => option.value === tag);
      if (found) return found;
      // If not found, treat it as a newly created tag with label=tag and value=tag
      return { value: tag, label: tag };
    });

  return (
    <div className="flex flex-col h-full space-y-10 text-[var(--text-primary)] font-[var(--font-ui)]">
      {/* Header */}
      <div className="space-y-1 border-b border-[var(--surface-800)] pb-4">
        <h1 className="text-2xl font-bold tracking-wide">Mission Management</h1>
        <p className="text-sm text-[var(--text-muted)]">Create and manage missions</p>
      </div>

      {/* Top Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
          <div className="flex items-center gap-3">
            <TbTargetArrow className="w-6 h-6 text-[var(--text-muted)]" />
            <span className="text-lg font-semibold text-[var(--text-muted)]">Total Missions</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">{statusCounts.total}</p>
        </div>
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <div
            key={key}
            onClick={() => setStatusFilter(key === statusFilter ? '' : key)}
            className={`cursor-pointer p-4 rounded-xl border transition
              ${statusFilter === key ? 'bg-[var(--surface-800)] border-[var(--signal-cyan)]' : 'bg-[var(--surface-900)] border-[var(--surface-800)]'}`}
          >
            <div className="flex items-center gap-3">
              <cfg.icon className={`w-6 h-6 ${cfg.color}`} />
              <span className={`text-lg font-semibold ${cfg.color}`}>{cfg.label}</span>
            </div>
            <p className="mt-2 text-3xl font-bold">{statusCounts[key]}</p>
          </div>
        ))}
      </div>

      {/* Create Button */}
      {role === 'admin' && (
        <div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
          >
            {showCreateForm ? <MinusCircleIcon className="w-5 h-5" /> : <PlusCircleIcon className="w-5 h-5" />}
            {showCreateForm ? 'Hide Create Form' : 'Create Mission'}
          </button>

          {showCreateForm && (
  <form className="mt-4 bg-[var(--surface-900)] border border-[var(--surface-800)] p-6 rounded-xl space-y-6">
    {/* Mission Details */}
    <section>
      <h2 className="text-lg font-semibold text-[var(--signal-cyan)] mb-4">Mission Details</h2>
     <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="title" className="block mb-1 text-[var(--text-muted)]">Mission Title <span className="text-[var(--status-error)]">*</span></label>
          <input
            id="title"
            type="text"
            required
            className="w-full px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            placeholder="Enter mission title"
            value={createForm.title}
            onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="description" className="block mb-1 text-[var(--text-muted)]">Description (optional)</label>
          <input
            id="description"
            type="text"
            className="w-full px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            placeholder="Add any additional details"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="status" className="block mb-1 text-[var(--text-muted)]">
            Status <span className="text-[var(--status-error)]">*</span>
          </label>
            <Listbox
              value={createForm.status || 'active'}
              onChange={(value) => setCreateForm({ ...createForm, status: value })}
            >
              <div className="relative">
                <Listbox.Button
                  className="w-full h-10 px-4 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md flex items-center justify-between
                            focus:border-[var(--signal-cyan)] focus:ring-1 focus:ring-[var(--signal-cyan)] transition-colors duration-200"
                >
                  <div className="flex items-center h-full">
                    <StatusBadge status={createForm.status || 'active'} />
                  </div>
                  <ChevronDown size={14} className="ml-2 text-[var(--text-primary)]" />
                </Listbox.Button>

                <Listbox.Options className="absolute mt-1 w-full bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md shadow-lg z-10">
                  {statuses.map((status) => (
                    <Listbox.Option
                      key={status}
                      value={status}
                      className="px-3 py-2 hover:bg-[var(--surface-800)]"
                    >
                      <StatusBadge status={status} />
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
          </Listbox>
        </div>
      </div>
    </section>

    {/* Schedule */}
    <section>
      <h2 className="text-lg font-semibold text-[var(--signal-cyan)] mb-4">Schedule</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="start_time" className="block mb-1 text-[var(--text-muted)]">Start Time <span className="text-[var(--status-error)]">*</span></label>
          <input
            id="start_time"
            type="datetime-local"
            required
            className="w-full px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            value={createForm.start_time}
            onChange={(e) => setCreateForm({ ...createForm, start_time: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="end_time" className="block mb-1 text-[var(--text-muted)]">End Time <span className="text-[var(--status-error)]">*</span></label>
          <input
            id="end_time"
            type="datetime-local"
            required
            className="w-full px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            value={createForm.end_time}
            onChange={(e) => setCreateForm({ ...createForm, end_time: e.target.value })}
          />
        </div>
      </div>
    </section>

    {/* Users & Sensors */}
    <section>
      <h2 className="text-lg font-semibold text-[var(--signal-cyan)] mb-4">Assign Users, Sensors, and Tags</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="users" className="block mb-1 text-[var(--text-muted)]">Users</label>
            <Select
              inputId="users"
              isMulti
              options={userOptions}
              value={userOptions.filter(option => createForm.users.includes(option.value))}
              onChange={(selectedOptions) => setCreateForm({ ...createForm, users: selectedOptions ? selectedOptions.map(o => o.value) : [] })}
              className="w-full"
              classNamePrefix="react-select"
              placeholder="Select users..."
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: 'var(--surface-800)',
                  primary: 'var(--signal-cyan)',
                  neutral0: 'var(--ink-950)',
                  neutral80: 'var(--surface-800)',
                },
              })}
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: 'var(--ink-950)',
                  borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  },
                }),
                multiValue: (base) => ({ ...base, backgroundColor: 'var(--surface-800)' }),
                multiValueLabel: (base) => ({ ...base, color: 'var(--text-primary)' }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'var(--text-primary)',
                  ':hover': { backgroundColor: 'var(--status-error)', color: 'white' },
                }),
                input: (base) => ({ ...base, color: 'var(--text-primary)' }),
              }}
              components={{ IndicatorSeparator, DropdownIndicator }}
            />

        </div>
        <div>
          <label htmlFor="sensors" className="block mb-1 text-[var(--text-muted)]">Sensors</label>
          <Select
              inputId="sensors"
              isMulti
              options={sensorOptions}
              value={sensorOptions.filter(option => createForm.sensors.includes(option.value))}
              onChange={(selectedOptions) => setCreateForm({ ...createForm, sensors: selectedOptions ? selectedOptions.map(o => o.value) : [] })}
              className="w-full"
              classNamePrefix="react-select"
              placeholder="Select sensors..."
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: 'var(--surface-800)',
                  primary: 'var(--signal-cyan)',
                  neutral0: 'var(--ink-950)',
                  neutral80: 'var(--surface-800)',
                },
              })}
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: 'var(--ink-950)',
                  borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  },
                }),
                multiValue: (base) => ({ ...base, backgroundColor: 'var(--surface-800)' }),
                multiValueLabel: (base) => ({ ...base, color: 'var(--text-primary)' }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'var(--text-primary)',
                  ':hover': { backgroundColor: 'var(--status-error)', color: 'white' },
                }),
                input: (base) => ({ ...base, color: 'var(--text-primary)' }),
              }}
              components={{ IndicatorSeparator, DropdownIndicator }}
            />
        </div>
        <div>
          <label htmlFor="tags" className="block mb-1 text-[var(--text-muted)]">Tags</label>
          <CreatableSelect
            inputId="tags"
            isMulti
            options={tagOptions} // fetched from /tags endpoint
            value={selectedTags}
            onChange={(selectedOptions) => 
              setCreateForm({ 
                ...createForm, 
                tags: selectedOptions ? selectedOptions.map(o => o.value || o.label) : [] 
              })
            }
            className="w-full"
            classNamePrefix="react-select"
            placeholder="Add or select tags..."
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: 'var(--surface-800)',
                primary: 'var(--signal-cyan)',
                neutral0: 'var(--ink-950)',
                neutral80: 'var(--surface-800)',
              },
            })}
            styles={{
              control: (base, state) => ({
                  ...base,
                  backgroundColor: 'var(--ink-950)',
                  borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)',
                  boxShadow: 'none', // optional: remove focus glow
                  '&:hover': {
                    borderColor: state.isFocused ? 'var(--signal-cyan)' : 'var(--surface-800)', // no hover change
                  },
                }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: 'var(--surface-800)',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: 'var(--text-primary)',
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: 'var(--text-primary)',
                ':hover': {
                  backgroundColor: 'var(--status-error)',
                  color: 'white',
                },
              }),
              input: (base) => ({
                ...base,
                color: 'var(--text-primary)',  // your desired text color inside the input
              }),
            }}
            components={{ IndicatorSeparator, DropdownIndicator }}
          />
        </div>
      </div>
    </section>

    {/* Submit Button */}
    <button
      type="button"
      onClick={handleCreate}
      disabled={!createForm.title || !createForm.start_time || !createForm.end_time || !createForm.status}
      className="mt-4 text-[var(--status-success)] border border-[var(--status-success)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition disabled:opacity-50"
    >
      Submit
    </button>
  </form>
)}

        </div>
      )}

      {/* Search */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-[var(--text-muted)]" />
          <input
            className="w-full pl-10 pr-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            placeholder="Search missions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full appearance-none pl-4 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
          </select>
          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl">
        <table className="min-w-full text-sm text-[var(--text-primary)]">
          <thead className="bg-[var(--surface-800)] text-[var(--text-muted)]">
            <tr>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => toggleSort('title')}>Title {sortKey === 'title' && (sortAsc ? '▲' : '▼')}</th>
              <th className="px-4 py-2 text-left cursor-pointer" onClick={() => toggleSort('description')}>Description {sortKey === 'description' && (sortAsc ? '▲' : '▼')}</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Users</th>
              <th className="px-4 py-2 text-left">Sensors</th>
              <th className="px-4 py-2 text-left">Tags</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMissions.map((m) => (
              <tr key={m.id} className="border-b border-[var(--surface-800)] hover:bg-[var(--surface-800)]">
                {editing?.id === m.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input className="px-2 py-1 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded w-full"
                        value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                    </td>
                    <td className="px-4 py-2">
                      <input className="px-2 py-1 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded w-full"
                        value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                    </td>
                    <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                    <td className="px-4 py-2">{m.user_count}</td>
                    <td className="px-4 py-2">{m.sensor_count}</td>
                    <td className="px-4 py-2">{m.tag_count}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button onClick={handleEdit} className="text-[var(--status-success)] border border-[var(--status-success)] px-2 py-1 rounded hover:bg-[var(--surface-800)]">Save</button>
                      <button onClick={() => { startEdit(null); }} className="text-[var(--text-muted)] border border-[var(--surface-800)] px-2 py-1 rounded hover:bg-[var(--surface-800)]">Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 font-medium">{m.title}</td>
                    <td className="px-4 py-2 text-[var(--text-muted)]">{m.description}</td>
                    <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                    <td className="px-4 py-2">{m.user_count}</td>
                    <td className="px-4 py-2">{m.sensor_count}</td>
                    <td className="px-4 py-2">{m.tag_count}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button onClick={() => navigate(`/mission-view/${m.id}`)} aria-label="View mission" title="View mission" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                        <ViewfinderCircleIcon className="w-5 h-5 inline" />
                      </button>
                      <button onClick={() => { startEdit(m)}} aria-label="Edit mission" title="Edit mission" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                        <PencilSquareIcon className="w-5 h-5 inline" />
                      </button>
                      <button onClick={() => handleDelete(m.id)} aria-label="Delete mission" title="Delete mission" className="text-[var(--status-error)] hover:text-[var(--text-primary)]">
                        <TrashIcon className="w-5 h-5 inline" />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {filteredMissions.length === 0 && (
              <tr><td colSpan="7" className="text-center py-6 text-[var(--text-muted)]">No matching missions found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
